import React, { useEffect, useMemo, useState } from 'react'
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    CircularProgress,
    makeStyles,
    Typography,
    TextField,
    InputAdornment,
    ButtonBase,
    Box,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import BusinessIcon from '@material-ui/icons/Business'
import { toast } from 'react-toastify'

import { GLOBAL_TOP_BAR_HEIGHT } from '../../../constants'
import { HeadCell } from '../../../components'
import { Company } from '../../../models'
import {
    OrganizationVendor,
    OrganizationVendorProperty,
} from '../../../hooks/useOrganizationManagement'
import { OrganizationVendorPropertiesDialog } from './OrganizationVendorPropertiesDialog'
import { VendorPerformanceDateFilters } from '../../VendorPerformance/VendorPerformanceDateFilters'
import { VendorPerformanceTable } from '../../VendorPerformance/VendorPerformanceTable'
import {
    VendorView,
    VendorViewPills,
} from '../../VendorPerformance/VendorViewPills'
import {
    DEFAULT_VENDOR_PERFORMANCE_PRESET,
    VendorPerformancePreset,
    getDefaultDateRange,
    getPresetRange,
    isDateRangeReady,
    isDateRangeValid,
} from '../../VendorPerformance/datePresets'
import { useVendorPerformanceList } from '../../../hooks/useVendorPerformance'
import { axiosInstance } from '../../../helpers'
import { typographyStyle } from '../../../theme/typographyStyle'

interface OrganizationVendorsRootProps {
    vendorList: OrganizationVendor[]
    allProperties: Company[]
    isLoading: boolean
    organizationId: number | null
}

const TABLE_HEAD_CELLS: HeadCell<{
    name: string
    invoice_code: string
    email: string
    in_house: string
    address: string
    manage_properties: string
}>[] = [
    {
        id: 'name',
        label: 'Name',
        align: 'left',
        disablePadding: false,
        width: 200,
    },
    {
        id: 'email',
        label: 'Email',
        align: 'left',
        disablePadding: false,
        width: 200,
    },
    {
        id: 'in_house',
        label: 'Type',
        align: 'left',
        disablePadding: false,
        width: 100,
    },
    {
        id: 'address',
        label: 'Address',
        align: 'left',
        disablePadding: false,
        width: 250,
    },
    {
        id: 'manage_properties',
        label: 'Manage Properties',
        align: 'center',
        disablePadding: false,
        width: 220,
    },
]

const useLocalStyles = makeStyles((theme) => ({
    contentWrapper: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: `calc(100vh - ${GLOBAL_TOP_BAR_HEIGHT}px)`,
        maxHeight: `calc(100vh - ${GLOBAL_TOP_BAR_HEIGHT}px)`,
        padding: theme.spacing(1.5, 2, 2),
        boxSizing: 'border-box',
        overflow: 'hidden',
    },
    analyticsHeader: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(2),
    },
    searchContainer: {
        display: 'flex',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    tableWrapper: {
        flex: 1,
        display: 'flex',
        minHeight: 0,
        overflow: 'auto',
        maxHeight: 'calc(100vh - 290px)',
        flexDirection: 'column',
    },
    loadingOverlay: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1,
    },
    stickyHeader: {
        backgroundColor: theme.palette.background.paper,
        position: 'sticky',
        top: 0,
        zIndex: 2,
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    table: {
        minWidth: 750,
    },
    tableRow: {
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        minHeight: 52,
        maxHeight: 52,
    },
    sortableHeader: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    managePropertiesCell: {
        padding: theme.spacing(0.5, 1),
    },
    vendorNameButton: {
        font: 'inherit',
        color: theme.palette.primary.main,
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        textAlign: 'left',
        fontWeight: 600,
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    managePropertiesButton: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(1),
        fontSize: typographyStyle(theme.typography.subtitle2).fontSize,
        fontWeight: 500,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(0.5, 1),
        cursor: 'pointer',
        transition: 'background-color 120ms ease, border-color 120ms ease',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
            borderColor: theme.palette.primary.main,
        },
        '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 1,
        },
    },
}))

export const OrganizationVendorsRoot = (
    props: OrganizationVendorsRootProps,
) => {
    const { vendorList, allProperties, isLoading, organizationId } = props
    const localClasses = useLocalStyles()
    const defaultRange = getDefaultDateRange()
    const [selectedView, setSelectedView] = useState<VendorView>('directory')
    const [preset, setPreset] = useState<VendorPerformancePreset | null>(
        DEFAULT_VENDOR_PERFORMANCE_PRESET,
    )
    const [startDate, setStartDate] = useState(defaultRange.startDate ?? '')
    const [endDate, setEndDate] = useState(defaultRange.endDate ?? '')
    const customRangeReady = isDateRangeReady(startDate, endDate)
    const customRangeValid = isDateRangeValid(startDate, endDate)
    const {
        rows: analyticsRows,
        isLoading: analyticsLoading,
        isError: analyticsError,
    } = useVendorPerformanceList({
        organizationId,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        enabled:
            selectedView === 'analytics' &&
            customRangeReady &&
            customRangeValid,
    })
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(50)
    const [searchText, setSearchText] = useState('')
    const [sortConfig, setSortConfig] = useState<{
        key: string
        direction: 'asc' | 'desc'
    } | null>(null)
    const [displayedVendorList, setDisplayedVendorList] = useState<
        OrganizationVendor[]
    >(vendorList)
    const [
        managePropertiesDialogOpen,
        setManagePropertiesDialogOpen,
    ] = useState<boolean>(false)
    const [selectedVendorId, setSelectedVendorId] = useState<number | null>(
        null,
    )

    useEffect(() => {
        setDisplayedVendorList(vendorList)
    }, [vendorList])

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const handleSort = (key: string) => {
        let direction: 'asc' | 'desc' = 'asc'
        if (
            sortConfig &&
            sortConfig.key === key &&
            sortConfig.direction === 'asc'
        ) {
            direction = 'desc'
        }
        setSortConfig({ key, direction })
    }

    const handleOpenManagePropertiesDialog = (vendorId: number) => {
        setSelectedVendorId(vendorId)
        setManagePropertiesDialogOpen(true)
    }

    const handleCloseManagePropertiesDialog = () => {
        setManagePropertiesDialogOpen(false)
    }

    const handleAddVendorToProperty = async (
        vendorId: number,
        property: Company,
    ) => {
        try {
            await axiosInstance.post(
                'company/vendor/org-add-vendor-apartment/',
                {
                    vendor_id: vendorId,
                    apartment_id: property.id,
                },
            )

            setDisplayedVendorList((previous) =>
                previous.map((vendor) => {
                    if (vendor.id !== vendorId) return vendor
                    if (vendor.properties.some((p) => p.id === property.id)) {
                        return vendor
                    }
                    const nextProperty: OrganizationVendorProperty = {
                        id: property.id,
                        name: property.name,
                    }
                    return {
                        ...vendor,
                        properties: [...vendor.properties, nextProperty],
                    }
                }),
            )
        } catch (error: any) {
            toast.error(
                error?.response?.data?.message ||
                    'Failed to add vendor to property',
            )
        }
    }

    const handleRemoveVendorFromProperty = async (
        vendorId: number,
        propertyId: number,
    ) => {
        try {
            await axiosInstance.post(
                'company/vendor/org-remove-vendor-apartment/',
                {
                    vendor_id: vendorId,
                    apartment_id: propertyId,
                },
            )

            setDisplayedVendorList((previous) =>
                previous.map((vendor) =>
                    vendor.id === vendorId
                        ? {
                              ...vendor,
                              properties: vendor.properties.filter(
                                  (property) => property.id !== propertyId,
                              ),
                          }
                        : vendor,
                ),
            )
        } catch (error: any) {
            toast.error(
                error?.response?.data?.message ||
                    'Failed to remove vendor from property',
            )
        }
    }

    const filteredAndSortedRows = useMemo(() => {
        const filtered = displayedVendorList.filter((vendor) => {
            const searchLower = searchText.toLowerCase()
            const matchesSearch =
                vendor.name.toLowerCase().includes(searchLower) ||
                vendor.email?.toLowerCase().includes(searchLower)
            return matchesSearch
        })

        if (sortConfig) {
            filtered.sort((a, b) => {
                let aValue: any
                let bValue: any

                switch (sortConfig.key) {
                    case 'name':
                        aValue = a.name
                        bValue = b.name
                        break
                    case 'invoice_code':
                        aValue = a.invoice_code
                        bValue = b.invoice_code
                        break
                    case 'email':
                        aValue = a.email
                        bValue = b.email
                        break
                    case 'in_house':
                        aValue = a.in_house
                        bValue = b.in_house
                        break
                    case 'address':
                        aValue = a.primary_address?.address1 || ''
                        bValue = b.primary_address?.address1 || ''
                        break
                    case 'manage_properties':
                        aValue = a.properties.length
                        bValue = b.properties.length
                        break
                    default:
                        return 0
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1
                }
                return 0
            })
        }

        return filtered
    }, [displayedVendorList, searchText, sortConfig])

    const paginatedRows = useMemo(() => {
        const startIndex = page * rowsPerPage
        return filteredAndSortedRows.slice(startIndex, startIndex + rowsPerPage)
    }, [filteredAndSortedRows, page, rowsPerPage])

    const selectedVendor =
        selectedVendorId === null
            ? null
            : displayedVendorList.find(
                  (vendor) => vendor.id === selectedVendorId,
              ) || null

    return (
        <div className={localClasses.contentWrapper}>
            <VendorViewPills value={selectedView} onChange={setSelectedView} />

            {selectedView === 'analytics' && (
                <div className={localClasses.analyticsHeader}>
                    <VendorPerformanceDateFilters
                        preset={preset}
                        startDate={startDate}
                        endDate={endDate}
                        onPresetChange={(nextPreset) => {
                            const range = getPresetRange(nextPreset)
                            setPreset(nextPreset)
                            setStartDate(range.startDate ?? '')
                            setEndDate(range.endDate ?? '')
                        }}
                        onStartDateChange={(value) => {
                            setPreset(null)
                            setStartDate(value)
                        }}
                        onEndDateChange={(value) => {
                            setPreset(null)
                            setEndDate(value)
                        }}
                    />
                    {!customRangeReady ? (
                        <Typography color="textSecondary">
                            Choose a start and end date to load analytics.
                        </Typography>
                    ) : !customRangeValid ? (
                        <Typography color="error">
                            Start date must be on or before end date.
                        </Typography>
                    ) : null}
                </div>
            )}

            {selectedView === 'directory' && (
                <>
                    <div className={localClasses.searchContainer}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search by vendor name or email..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </div>

                    <TableContainer
                        component={Paper}
                        className={localClasses.tableWrapper}
                    >
                        <Table
                            className={localClasses.table}
                            size="small"
                            stickyHeader
                        >
                            <TableHead>
                                <TableRow>
                                    {TABLE_HEAD_CELLS.map((cell) => (
                                        <TableCell
                                            key={cell.id}
                                            align={cell.align}
                                            padding={
                                                cell.disablePadding
                                                    ? 'none'
                                                    : 'default'
                                            }
                                            className={`${
                                                localClasses.stickyHeader
                                            } ${
                                                cell.id !== 'manage_properties'
                                                    ? localClasses.sortableHeader
                                                    : ''
                                            }`}
                                            style={{ width: cell.width }}
                                            onClick={
                                                cell.id !== 'manage_properties'
                                                    ? () => handleSort(cell.id)
                                                    : undefined
                                            }
                                        >
                                            {cell.label}
                                            {sortConfig?.key === cell.id && (
                                                <span>
                                                    {sortConfig.direction ===
                                                    'asc'
                                                        ? ' ↑'
                                                        : ' ↓'}
                                                </span>
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedRows.map((vendor) => (
                                    <TableRow
                                        key={`ROW_${vendor.id}`}
                                        hover
                                        className={localClasses.tableRow}
                                    >
                                        <TableCell>{vendor.name}</TableCell>
                                        <TableCell>{vendor.email}</TableCell>
                                        <TableCell>
                                            {vendor.in_house
                                                ? 'In House'
                                                : 'Contractor'}
                                        </TableCell>
                                        <TableCell>
                                            {vendor.primary_address ? (
                                                <div>
                                                    <Typography variant="subtitle2">
                                                        {
                                                            vendor
                                                                .primary_address
                                                                .address1
                                                        }
                                                    </Typography>
                                                    <Typography variant="subtitle2">
                                                        {
                                                            vendor
                                                                .primary_address
                                                                .city
                                                        }
                                                        ,{' '}
                                                        {
                                                            vendor
                                                                .primary_address
                                                                .state
                                                        }{' '}
                                                        {
                                                            vendor
                                                                .primary_address
                                                                .zip_code
                                                        }
                                                    </Typography>
                                                </div>
                                            ) : (
                                                <Typography variant="subtitle2">
                                                    N/A
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box
                                                className={
                                                    localClasses.managePropertiesCell
                                                }
                                            >
                                                <ButtonBase
                                                    className={
                                                        localClasses.managePropertiesButton
                                                    }
                                                    onClick={() =>
                                                        handleOpenManagePropertiesDialog(
                                                            vendor.id,
                                                        )
                                                    }
                                                >
                                                    <span>
                                                        {
                                                            vendor.properties
                                                                .length
                                                        }
                                                    </span>
                                                    <BusinessIcon fontSize="small" />
                                                </ButtonBase>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <div className={localClasses.footer}>
                        <TablePagination
                            count={filteredAndSortedRows.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onChangePage={handleChangePage}
                            onChangeRowsPerPage={handleChangeRowsPerPage}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                        />
                    </div>

                    {isLoading && (
                        <div className={localClasses.loadingOverlay}>
                            <CircularProgress />
                        </div>
                    )}
                </>
            )}

            {selectedView === 'analytics' &&
                customRangeReady &&
                customRangeValid && (
                    <VendorPerformanceTable
                        rows={analyticsRows}
                        isLoading={analyticsLoading}
                        isError={analyticsError}
                    />
                )}

            <OrganizationVendorPropertiesDialog
                open={managePropertiesDialogOpen}
                onClose={handleCloseManagePropertiesDialog}
                vendor={selectedVendor}
                allProperties={allProperties}
                onAddProperty={handleAddVendorToProperty}
                onRemoveProperty={handleRemoveVendorFromProperty}
            />
        </div>
    )
}
